/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';

import { Alert, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import { Namespaces } from '../../i18n';
import { RootState } from '../../store';
import { useMiscellaneousStyles } from '../../styles/miscellaneous';
import { fetchJiraConfig } from '../../utils/queryFns';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { JiraSupport } from './jira/JiraSupport';
import { useSupportPageStyles } from './SupportPage.styles';

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
});
const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type IProps = PropsFromRedux;

const SupportPageComponent = memo<IProps>(({ auth }) => {
  const { t } = useTranslation(Namespaces.General);
  const classes = useSupportPageStyles();
  const { loaderContainer } = useMiscellaneousStyles();

  useDocumentTitle(t(`${Namespaces.Common}:page_titles.support`));

  const { data: jiraConfigData, isLoading: isConfigLoading } = useQuery(fetchJiraConfig.key, fetchJiraConfig.fn, {
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const configExists = useMemo(() => jiraConfigData?.payload.status ?? false, [jiraConfigData]);

  return (
    <div className={classes.root}>
      {isConfigLoading ? (
        <AlignCenter className={loaderContainer}>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : configExists ? (
        <JiraSupport user={auth.user} />
      ) : (
        <Alert severity="warning">
          <Trans
            i18nKey="general:jira_support.notConfiguredUser"
            components={{
              b: <b />,
            }}
          />
        </Alert>
      )}
    </div>
  );
});

export const SupportPage = connector(SupportPageComponent);
